/**
 * Entscheidung im Verlauf (Board „Verlauf · Entscheidungen“): ein Zeitstempel („Berechnet“), der
 * Aufenthaltstag als „Fr 30.10.“, Preis und Mindestnächte bestätigt → Ziel ohne Kürzel. Die
 * Herleitung zeigt `PriceDerivation`.
 */
import type { DynamicPricingDecisionHistoryItem } from '@/shared/api';
import { warningCodeText } from '../pricingCodeText';
import type { HistoryDetail } from './historyDetailModel';
import { historyChange, historyDateTime, historyDay, historyMoney } from './historyFormat';
import { OUTCOME, TRIGGER, label, type Tone } from './historyText';

export const decisionOutcome = (outcome: string) =>
  OUTCOME[outcome] ?? { label: outcome, tone: 'grey' as Tone };

export function decisionValue(item: DynamicPricingDecisionHistoryItem) {
  const money = (value: number | null) => historyMoney(value, item.currency);
  const stay = (value: number | null) => (value === null ? '—' : String(value));
  return `${historyChange(item.confirmedRate, item.desiredRate, money)} · ${historyChange(
    item.confirmedMinimumStay,
    item.desiredMinimumStay,
    stay,
  )}`;
}

export function decisionCells(item: DynamicPricingDecisionHistoryItem) {
  return {
    at: historyDateTime(item.calculatedAtUtc, item.scope.timezone),
    apartment: item.scope.propertyName,
    day: historyDay(item.stayDate),
    trigger: label(TRIGGER, item.trigger),
    outcome: decisionOutcome(item.outcome),
    value: decisionValue(item),
  };
}

export function decisionDetail(item: DynamicPricingDecisionHistoryItem): HistoryDetail {
  const cells = decisionCells(item);
  const money = (value: number | null) => historyMoney(value, item.currency);
  const stay = (value: number | null) => (value === null ? '—' : String(value));
  return {
    eyebrow: `Entscheidung · berechnet ${cells.at}`,
    title: `${cells.apartment} · ${cells.day}`,
    facts: [
      ['Aufenthaltstag', cells.day],
      ['Auslöser', cells.trigger],
      ['Ergebnis', cells.outcome.label],
      ['Preis bestätigt → Ziel', historyChange(item.confirmedRate, item.desiredRate, money)],
      [
        'Mindestnächte bestätigt → Ziel',
        historyChange(item.confirmedMinimumStay, item.desiredMinimumStay, stay),
      ],
    ],
    box: item.warningCodes.length
      ? {
          title: 'Hinweise',
          lines: item.warningCodes.map((code) => ({
            text: warningCodeText(code) ?? 'Hinweis zur Entscheidung',
            code,
            tone: 'amber' as Tone,
          })),
        }
      : undefined,
    refs: [
      ['Entscheidung', item.id],
      ['Lauf', item.runId],
      ['Anfrage', item.requestId],
      ['Einstellungs-Version', item.configurationVersionId],
      ['Strategie-Version', item.strategyProfileVersionId ?? '—'],
    ],
  };
}
